import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Types
export type ParameterType =
  | 'string'
  | 'number'
  | 'boolean'
  | 'object'
  | 'array';

export interface ArrayItemsSchema {
  type: 'object';
  itemsName?: string;

  properties: Record<
    string,
    {
      type: 'string' | 'number' | 'boolean';
      description?: string;
      required?: boolean;
    }
  >;
  required?: string[];
}

export interface FunctionParameter {
  name: string;
  type: ParameterType;
  description: string;
  required: boolean;
  arrayItems?: ArrayItemsSchema;
}

export interface ParameterMapping {
  functionParameter?: string; // Parameter name
  apiParameter?: string; // API parameter name
  location?: 'params' | 'body' | 'headers' | 'path';
  value?: string | number;
}

export interface ResultMapping {
  resultField: string;
  apiResponsePath: string;
}

export interface ApiConfig {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  endpoint: string;
  queryParams: { key: string; value: string }[];
  processingMessage: string;
}

export interface FunctionBuilderState {
  functionName: string;
  functionDescription: string;
  parameters: FunctionParameter[];
  apiConfig: ApiConfig;
  parameterMappings: ParameterMapping[];
  resultMappings: ResultMapping[];
}

const initialState: FunctionBuilderState = {
  functionName: '',
  functionDescription: '',
  parameters: [],
  apiConfig: {
    method: 'GET',
    endpoint: '',
    queryParams: [],
    processingMessage: '',
  },
  parameterMappings: [],
  resultMappings: [],
};

export const webhookFunctionSlice = createSlice({
  name: 'webhookFunction',
  initialState,
  reducers: {
    // Function details
    setFunctionName: (state, action: PayloadAction<string>) => {
      state.functionName = action.payload;
    },
    setFunctionDescription: (state, action: PayloadAction<string>) => {
      state.functionDescription = action.payload;
    },

    // Parameters
    addParameter: (state, action: PayloadAction<FunctionParameter>) => {
      state.parameters.push(action.payload);
    },
    updateParameter: (
      state,
      action: PayloadAction<{ index: number; parameter: FunctionParameter }>
    ) => {
      const { index, parameter } = action.payload;
      if (index >= 0 && index < state.parameters.length) {
        state.parameters[index] = parameter;
      }
    },
    removeParameter: (state, action: PayloadAction<number>) => {
      state.parameters.splice(action.payload, 1);
    },

    // API Config
    setApiMethod: (
      state,
      action: PayloadAction<'GET' | 'POST' | 'PUT' | 'DELETE'>
    ) => {
      state.apiConfig.method = action.payload;
    },
    setApiEndpoint: (state, action: PayloadAction<string>) => {
      state.apiConfig.endpoint = action.payload;
    },
    addQueryParam: (
      state,
      action: PayloadAction<{ key: string; value: string }>
    ) => {
      state.apiConfig.queryParams.push(action.payload);
    },
    updateQueryParam: (
      state,
      action: PayloadAction<{
        index: number;
        queryParam: { key: string; value: string };
      }>
    ) => {
      const { index, queryParam } = action.payload;
      if (index >= 0 && index < state.apiConfig.queryParams.length) {
        state.apiConfig.queryParams[index] = queryParam;
      }
    },
    removeQueryParam: (state, action: PayloadAction<number>) => {
      state.apiConfig.queryParams.splice(action.payload, 1);
    },
    setProcessingMessage: (state, action: PayloadAction<string>) => {
      state.apiConfig.processingMessage = action.payload;
    },

    // Parameter Mapping
    addParameterMapping: (
      state,
      action: PayloadAction<Partial<ParameterMapping>>
    ) => {
      state.parameterMappings.push(action.payload);
    },
    updateParameterMapping: (
      state,
      action: PayloadAction<{
        index: number;
        mapping: ParameterMapping;
      }>
    ) => {
      const { index, mapping } = action.payload;
      if (index >= 0 && index < state.parameterMappings.length) {
        state.parameterMappings[index] = mapping;
      }
    },
    removeParameterMapping: (state, action: PayloadAction<number>) => {
      state.parameterMappings.splice(action.payload, 1);
    },

    // Result Mapping
    addResultMapping: (state, action: PayloadAction<ResultMapping>) => {
      state.resultMappings.push(action.payload);
    },
    updateResultMapping: (
      state,
      action: PayloadAction<{
        index: number;
        mapping: ResultMapping;
      }>
    ) => {
      const { index, mapping } = action.payload;
      if (index >= 0 && index < state.resultMappings.length) {
        state.resultMappings[index] = mapping;
      }
    },
    removeResultMapping: (state, action: PayloadAction<number>) => {
      state.resultMappings.splice(action.payload, 1);
    },

    // Reset
    resetFunctionBuilder: () => initialState,
  },
});

export const {
  setFunctionName,
  setFunctionDescription,
  addParameter,
  updateParameter,
  removeParameter,
  setApiMethod,
  setApiEndpoint,
  addQueryParam,
  updateQueryParam,
  removeQueryParam,
  setProcessingMessage,
  addParameterMapping,
  updateParameterMapping,
  removeParameterMapping,
  addResultMapping,
  updateResultMapping,
  removeResultMapping,
  resetFunctionBuilder,
} = webhookFunctionSlice.actions;

export default webhookFunctionSlice.reducer;
